import { css, LitElement, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
import { MediaQueryController } from "../shared/core/controllers/MediaQueryController";
import { ThemeController } from "../shared/core/controllers/ThemeController";
import { TimelineController } from "../shared/core/controllers/TimelineController";
import bigO from "../../static/images/big-o.svg";
import reservoir from "../../static/images/reservoir-sampling.svg";
import turing from "../../static/images/turing-machine.svg";
import bloom from "../../static/images/bloom-filters.svg";
import hashing from "../../static/images/hashing.svg";
import memory from "../../static/images/memory-allocation.svg";
import balancing from "../../static/images/load-balancing.svg";
import queueing from "../../static/images/queueing.svg";
import retries from "../../static/images/retries.svg";
import caching from "../../static/images/prompt-caching.svg";
import quantization from "../../static/images/quantization.svg";
import probes from "../../static/images/probes.svg";

const artwork = {
	"big-o": bigO,
	"reservoir-sampling": reservoir,
	"turing-machine": turing,
	"bloom-filters": bloom,
	hashing,
	"memory-allocation": memory,
	"load-balancing": balancing,
	queueing,
	retries,
	"prompt-caching": caching,
	quantization,
	probes,
};

@customElement("s-card-animation")
export class CardAnimation extends LitElement {
	private static active?: CardAnimation;

	@property() name: keyof typeof artwork = "big-o";

	@property({ type: Number, attribute: "data-freeze-frame" })
	freezeFrame = 0.5;

	private readonly motion = new MediaQueryController(
		this,
		"(prefers-reduced-motion: reduce)",
	);

	private readonly timeline = new TimelineController(this);

	private registered = new WeakSet<Animation>();

	private card?: Element;

	private link?: HTMLAnchorElement;

	private needsInitialization = true;

	constructor() {
		super();
		this.motion.addEventListener("change", this.syncPlayback);
		new ThemeController(this).addEventListener("change", this.syncTheme);
	}

	static override styles = css`
		:host {
			display: block;
			position: relative;
			width: 100%;
			aspect-ratio: 3 / 2;
			contain: strict;
			pointer-events: none;
		}
		:host > svg {
			position: absolute;
			inset: 0;
			display: block;
			width: 100%;
			height: 100%;
		}
	`;

	override connectedCallback(): void {
		super.connectedCallback();
		this.needsInitialization = true;
		this.card = this.closest(".card") ?? undefined;
		this.link = this.closest("a") ?? undefined;
		this.card?.addEventListener("pointermove", this.syncPlayback);
		this.card?.addEventListener("pointerleave", this.syncPlayback);
		this.link?.addEventListener("focus", this.syncPlayback);
		this.link?.addEventListener("blur", this.syncPlayback);
		window.addEventListener("blur", this.pausePlayback);
		window.addEventListener("pageshow", this.pausePlayback);
		document.addEventListener("visibilitychange", this.pausePlayback);
		this.requestUpdate();
	}

	override disconnectedCallback(): void {
		this.pausePlayback();
		this.card?.removeEventListener("pointermove", this.syncPlayback);
		this.card?.removeEventListener("pointerleave", this.syncPlayback);
		this.link?.removeEventListener("focus", this.syncPlayback);
		this.link?.removeEventListener("blur", this.syncPlayback);
		window.removeEventListener("blur", this.pausePlayback);
		window.removeEventListener("pageshow", this.pausePlayback);
		document.removeEventListener("visibilitychange", this.pausePlayback);
		this.registered = new WeakSet();
		this.classList.remove("animation-ready");
		super.disconnectedCallback();
	}

	protected override updated(changed: PropertyValues<this>): void {
		if (
			this.needsInitialization ||
			changed.has("name") ||
			changed.has("freezeFrame")
		) {
			this.needsInitialization = false;
			this.syncTheme();
			// A stationary pointer must not start playback during upgrade.
			this.pausePlayback();
			this.classList.add("animation-ready");
		}
	}

	private readonly syncTheme = (): void => {
		const root = this.renderRoot?.querySelector("svg");
		if (root) {
			root.dataset.theme = this.dataset.theme ?? "light";
			root.style.backgroundColor = "transparent";
		}
	};

	private readonly syncPlayback = (): void => {
		this.setPlaying(
			this.card?.matches(":hover") === true ||
				this.link?.matches(":focus-visible") === true,
		);
	};

	private readonly pausePlayback = (): void => this.setPlaying(false);

	private setPlaying(playing: boolean): void {
		const animations =
			this.renderRoot.querySelector("svg")?.getAnimations({ subtree: true }) ??
			[];
		const added = new Set<Animation>();
		for (const animation of animations) {
			if (!this.registered.has(animation)) {
				this.timeline.registerAnimation(animation);
				this.registered.add(animation);
				added.add(animation);
			}
		}
		const shouldPlay = playing && !this.motion.matches;
		if (shouldPlay) {
			if (CardAnimation.active !== this) {
				CardAnimation.active?.pausePlayback();
			}
			CardAnimation.active = this;
			this.timeline.resume();
		} else {
			if (CardAnimation.active === this) {
				CardAnimation.active = undefined;
			}
			this.timeline.pause();
		}
		for (const animation of animations) {
			if (!shouldPlay || added.has(animation)) {
				const duration = Number(animation.effect?.getComputedTiming().duration);
				if (Number.isFinite(duration)) {
					// Seek after pausing; a pending pause must not advance the still frame.
					animation.currentTime = duration * this.freezeFrame;
				}
			}
		}
	}

	protected override render() {
		// Only authored assets enter unsafeSVG. Scope root rules to the outer SVG,
		// not nested playing cards; retain the controlled reduced-motion still frame.
		return unsafeSVG(
			artwork[this.name]
				.replaceAll(":root", ":host > svg")
				.replace("<svg", "<svg data-card-controlled"),
		);
	}
}
